import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Camera, Save, X, AlertTriangle, CheckCircle } from 'lucide-react';
// Removed apiService import to avoid build issues

interface Camera {
  _id: string;
  name: string;
  rtsp_url: string;
  location?: string;
  restricted_zone?: number[][];
}

interface RestrictedAreaSetupProps {
  onClose: () => void;
}

// Local API request function to avoid import issues
const makeApiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8006';
  const token = localStorage.getItem('csense_auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export function RestrictedAreaSetup({ onClose }: RestrictedAreaSetupProps) {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygonPoints, setPolygonPoints] = useState<number[][]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    loadCameras();
  }, []);

  const loadCameras = async () => {
    try {
      const response = await makeApiRequest('/api/user/cameras', {});
      setCameras(response.cameras || []);
    } catch (error) {
      console.error('Failed to load cameras:', error);
      setError('Failed to load cameras');
    }
  };

  const handleCameraSelect = async (cameraId: string) => {
    const camera = cameras.find(c => c._id === cameraId);
    if (!camera) return;

    setSelectedCamera(camera);
    setPolygonPoints(camera.restricted_zone || []);
    setError(null);
    setSuccess(null);

    // Load camera stream
    if (videoRef.current) {
      try {
        videoRef.current.src = `/api/cameras/${camera._id}/live-stream`;
        videoRef.current.load();
      } catch (error) {
        console.error('Failed to load camera stream:', error);
      }
    }
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / canvas.width;
    const y = (event.clientY - rect.top) / canvas.height;

    setPolygonPoints(prev => [...prev, [x, y]]);
  };

  const startDrawing = () => {
    setIsDrawing(true);
    setPolygonPoints([]);
  };

  const finishDrawing = () => {
    setIsDrawing(false);
    if (polygonPoints.length < 3) {
      setError('Please draw at least 3 points to create a zone');
      setPolygonPoints([]);
    }
  };

  const clearZone = () => {
    setPolygonPoints([]);
    setIsDrawing(false);
  };

  const saveZone = async () => {
    if (!selectedCamera || polygonPoints.length < 3) {
      setError('Please select a camera and draw a valid zone');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/restricted-area/set-zone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('csense_auth_token')}`
        },
        body: JSON.stringify({
          camera_id: selectedCamera._id,
          polygon_points: polygonPoints
        })
      });

      if (response.ok) {
        setSuccess('Restricted zone saved successfully!');
        // Update camera in local state
        setCameras(prev => prev.map(c => 
          c._id === selectedCamera._id 
            ? { ...c, restricted_zone: polygonPoints }
            : c
        ));
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save zone');
      }
    } catch (error) {
      console.error('Failed to save zone:', error);
      setError('Failed to save zone');
    } finally {
      setLoading(false);
    }
  };

  // Draw polygon on canvas
  useEffect(() => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (polygonPoints.length > 0) {
      ctx.strokeStyle = '#ef4444';
      ctx.fillStyle = 'rgba(239, 68, 68, 0.2)';
      ctx.lineWidth = 2;

      ctx.beginPath();
      const firstPoint = polygonPoints[0];
      ctx.moveTo(firstPoint[0] * canvas.width, firstPoint[1] * canvas.height);

      for (let i = 1; i < polygonPoints.length; i++) {
        const point = polygonPoints[i];
        ctx.lineTo(point[0] * canvas.width, point[1] * canvas.height);
      }

      if (polygonPoints.length > 2 && !isDrawing) {
        ctx.closePath();
        ctx.fill();
      }
      ctx.stroke();

      // Draw points
      polygonPoints.forEach(point => {
        ctx.beginPath();
        ctx.arc(point[0] * canvas.width, point[1] * canvas.height, 4, 0, 2 * Math.PI);
        ctx.fillStyle = '#ef4444';
        ctx.fill();
      });
    }
  }, [polygonPoints, isDrawing]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Restricted Area Detection Setup
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Camera Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Camera</label>
            <Select onValueChange={handleCameraSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a camera to configure" />
              </SelectTrigger>
              <SelectContent>
                {cameras.map(camera => (
                  <SelectItem key={camera._id} value={camera._id}>
                    <div className="flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      {camera.name}
                      {camera.restricted_zone && (
                        <Badge variant="outline" className="text-xs">
                          Zone Set
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCamera && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Configure Zone for {selectedCamera.name}
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startDrawing}
                    disabled={isDrawing}
                  >
                    {isDrawing ? 'Drawing...' : 'Start Drawing'}
                  </Button>
                  {isDrawing && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={finishDrawing}
                    >
                      Finish Zone
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearZone}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* Video Stream with Canvas Overlay */}
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-64 object-cover"
                  autoPlay
                  muted
                  playsInline
                />
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 w-full h-full cursor-crosshair"
                  width={640}
                  height={360}
                  onClick={handleCanvasClick}
                />
                {isDrawing && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm">
                    Click to add points • {polygonPoints.length} points
                  </div>
                )}
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>• Click "Start Drawing" to begin creating a restricted zone</p>
                <p>• Click on the video to add points for the zone boundary</p>
                <p>• Click "Finish Zone" when you have at least 3 points</p>
                <p>• The system will alert when a person enters this area</p>
              </div>

              {polygonPoints.length >= 3 && !isDrawing && (
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={saveZone}
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Restricted Zone'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}